import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Icon, type IconName } from '@/design-system/atoms/Icon'
import { IconButton } from '@/design-system/atoms/IconButton'

const alertVariants = cva(
  'relative flex gap-3 rounded-md border-l-4 bg-white p-4 text-sm shadow-sm dark:bg-gray-900',
  {
    variants: {
      variant: {
        info: 'border-l-blue-500 text-gray-800 dark:text-gray-200',
        success: 'border-l-green-500 text-gray-800 dark:text-gray-200',
        warning: 'border-l-amber-500 text-gray-800 dark:text-gray-200',
        error: 'border-l-red-500 text-gray-800 dark:text-gray-200',
      },
    },
    defaultVariants: { variant: 'info' },
  }
)

const variantIcon: Record<
  NonNullable<VariantProps<typeof alertVariants>['variant']>,
  { name: IconName; color: string }
> = {
  info: { name: 'info', color: 'text-blue-500' },
  success: { name: 'check-circle', color: 'text-green-500' },
  warning: { name: 'alert-triangle', color: 'text-amber-500' },
  error: { name: 'x-circle', color: 'text-red-500' },
}

export interface AlertProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertVariants> {
  title?: React.ReactNode
  icon?: React.ReactNode
  onClose?: () => void
  closeLabel?: string
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      variant = 'info',
      title,
      children,
      icon,
      onClose,
      closeLabel = 'Fechar aviso',
      ...rest
    },
    ref
  ) => {
    const v = variantIcon[variant ?? 'info']
    return (
      <div
        ref={ref}
        role={variant === 'error' || variant === 'warning' ? 'alert' : 'status'}
        aria-live={
          variant === 'error' || variant === 'warning' ? 'assertive' : 'polite'
        }
        className={cn(alertVariants({ variant, className }))}
        {...rest}
      >
        <span className={cn('mt-0.5 shrink-0', v.color)}>
          {icon ?? <Icon name={v.name} size={20} />}
        </span>
        <div className="flex-1">
          {title && <p className="font-semibold leading-tight">{title}</p>}
          {children && (
            <div
              className={cn(
                title ? 'mt-1 text-gray-600 dark:text-gray-400' : ''
              )}
            >
              {children}
            </div>
          )}
        </div>
        {onClose && (
          <IconButton
            variant="ghost"
            size="sm"
            icon={<Icon name="x" size={16} />}
            aria-label={closeLabel}
            onClick={onClose}
          />
        )}
      </div>
    )
  }
)

Alert.displayName = 'Alert'

export { alertVariants }
